/**
 * URL <-> filter-state mapping for the lane pages (Tasks Rebuild Task 8).
 *
 * Modelled on the legacy Tasks.tsx's tasksView.ts (deleted in Task 14's hard
 * cutover): the URL is the single source of truth for lane state -- filters,
 * sort, pagination and the selected task all
 * round-trip through query params so a lane view is shareable/refresh-proof.
 * Path (`/tasks/:lane`) carries the lane itself; everything else lives in
 * the query string.
 */
import { LaneId, LANE_IDS } from '../../api/laneApi';

export type LaneTab = 'pending' | 'completed';

export const DEFAULT_LANE: LaneId = 'client';
export const DEFAULT_PAGE_SIZE = 50;

/** Derives the active lane from a React Router pathname like `/tasks/lender`.
 * Falls back to the default lane for anything unrecognised. */
export function laneFromPath(pathname: string): LaneId {
  const segment = pathname.split('/').filter(Boolean)[1];
  return (LANE_IDS as string[]).includes(segment || '') ? (segment as LaneId) : DEFAULT_LANE;
}

/** Sentinel value for `receivedFrom` meaning "the user explicitly dismissed
 * this default filter" -- see `receivedFromDismissed` below. Deliberately
 * distinct from deleting the param outright: a lane page needs to tell
 * "never touched" (absent, apply the default) apart from "touched and
 * turned off" (present as 'off', stay off across remounts) so a default-on
 * chip can survive ordinary SPA unmount/remount navigation without the
 * dismissed filter silently coming back. */
export const RECEIVED_FROM_OFF = 'off';

export interface LaneFilters {
  tab: LaneTab;
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
  /** The active date filter value, or null if there is none (either never
   * set, or explicitly dismissed -- see `receivedFromDismissed` to tell
   * those two apart). Never the raw 'off' sentinel -- callers that build API
   * requests from this field can treat null as "send nothing" and can never
   * accidentally forward `receivedFrom=off` to the backend. */
  receivedFrom: string | null;
  /** True iff the URL's `receivedFrom` param is the literal 'off' sentinel,
   * i.e. the user explicitly dismissed the default filter. Distinguishes
   * that from "never touched" (both leave `receivedFrom` null above). */
  receivedFromDismissed: boolean;
  senderEmail: string | null;
  claim: string | null;
  assigned: string | null;
  q: string | null;
  archived: boolean;
  selected: string | null;
  /** The omnibox's `until:` chip value (ISO `YYYY-MM-DD`), or null. Carried
   * in the URL ("everything in URL" -- see Omnibox.tsx). Task 13 (omnibox)
   * introduced this field display-only; #4 wired it to the backend's
   * `received_date < until + 1 day` predicate (task_lane_service.py's
   * receivedTo) -- ClientLane/LenderLane now forward it as `receivedTo` on
   * every list fetch (not ManualLane, which has no received-date concept at
   * all, same as receivedFrom). */
  until: string | null;
  /** The manual lane's Task type dropdown (70+ hold build, 2026-08-18) --
   * 'ringfence' | 'standard' | null (all). Forwarded verbatim as the
   * backend's `taskSource` param; unknown values are the backend's problem
   * (its whitelist treats them as no filter, never hiding rows). */
  taskSource: string | null;
}

export function filtersFromSearchParams(params: URLSearchParams): LaneFilters {
  const rawPage = parseInt(params.get('page') || '1', 10);
  const rawPageSize = parseInt(params.get('pageSize') || '', 10);
  const sortOrderParam = params.get('sortOrder');
  const rawReceivedFrom = params.get('receivedFrom');
  return {
    tab: params.get('tab') === 'completed' ? 'completed' : 'pending',
    page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
    pageSize: Number.isFinite(rawPageSize) && rawPageSize > 0 ? rawPageSize : DEFAULT_PAGE_SIZE,
    sortBy: params.get('sortBy'),
    sortOrder: sortOrderParam === 'asc' || sortOrderParam === 'desc' ? sortOrderParam : null,
    receivedFrom: rawReceivedFrom === RECEIVED_FROM_OFF ? null : rawReceivedFrom,
    receivedFromDismissed: rawReceivedFrom === RECEIVED_FROM_OFF,
    senderEmail: params.get('senderEmail'),
    claim: params.get('claim'),
    assigned: params.get('assigned'),
    q: params.get('q'),
    archived: params.get('archived') === 'yes',
    selected: params.get('selected'),
    until: params.get('until'),
    taskSource: params.get('taskSource'),
  };
}

/** Switches Pending/Completed. Resets page + selection since the two tabs
 * are independent result sets. */
export function applyTab(params: URLSearchParams, tab: LaneTab): URLSearchParams {
  if (tab === 'completed') params.set('tab', 'completed');
  else params.delete('tab');
  params.delete('page');
  params.delete('selected');
  return params;
}

export function applyPage(params: URLSearchParams, page: number): URLSearchParams {
  if (page > 1) params.set('page', String(page));
  else params.delete('page');
  return params;
}

export function applySelectedTask(params: URLSearchParams, taskId: string | null): URLSearchParams {
  if (taskId) params.set('selected', taskId);
  else params.delete('selected');
  return params;
}

/** Free-text omnibox query (Task 13 will extend this with parsed operator
 * chips); resets pagination since the result set changes. */
export function applyQuery(params: URLSearchParams, q: string): URLSearchParams {
  if (q) params.set('q', q);
  else params.delete('q');
  params.delete('page');
  return params;
}

export function applySort(
  params: URLSearchParams, sortBy: string | null, sortOrder: 'asc' | 'desc' | null,
): URLSearchParams {
  if (sortBy) params.set('sortBy', sortBy); else params.delete('sortBy');
  if (sortOrder) params.set('sortOrder', sortOrder); else params.delete('sortOrder');
  return params;
}

export function applyArchived(params: URLSearchParams, archived: boolean): URLSearchParams {
  if (archived) params.set('archived', 'yes');
  else params.delete('archived');
  params.delete('page');
  return params;
}

export function applyFilter(
  params: URLSearchParams,
  key: 'receivedFrom' | 'senderEmail' | 'claim' | 'assigned' | 'until' | 'taskSource',
  value: string | null,
): URLSearchParams {
  if (value) params.set(key, value);
  else params.delete(key);
  params.delete('page');
  return params;
}
